import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/**
 * Přidává do textových inputů ag-grid filtrů křížek pro vymazání zadaného textu.
 *
 * V aplikaci je ~114 gridů a stovky colDefů s nativním `agTextColumnFilter`, které
 * nemají žádné společné konfigurační místo (žádná base grid komponenta, `defaultColDef`
 * je duplikovaný v každé komponentě). Proto se křížek přidává jedním globálním
 * MutationObserverem nad `document.body`, který dekoruje filter popupy ve chvíli,
 * kdy je ag-grid vytvoří.
 *
 * Vymazání se dělá vyvoláním nativního `input` eventu — ag-grid na něj interně
 * poslouchá (`addManagedElementListeners(this.eInput, { input: e => this.setValue(...) })`),
 * takže se korektně přepíše filter model a spustí `filterChangedCallback`.
 *
 * POZOR: řešení je navázané na interní DOM strukturu ag-gridu (v32, legacy theming).
 * Při upgradu na v33+ / Theming API je potřeba překontrolovat selektory v
 * `INPUT_SELECTOR` a odpovídající styly ve `src/styles/vip-ag-grid.styles.scss`.
 */
@Injectable({ providedIn: 'root' })
export class AgGridFilterClearService {
    /** Textové inputy uvnitř filter popupu (pokrývá i number/date filtry). */
    private static readonly INPUT_SELECTOR = '.ag-filter-body input.ag-input-field-input';

    /** Kontejnery, do kterých ag-grid vkládá filter popup. */
    private static readonly POPUP_SELECTOR = '.ag-popup, .ag-menu, .ag-filter, .ag-filter-body';

    private static readonly DECORATED_FLAG = 'vipFilterClear';
    private static readonly BUTTON_CLASS = 'vip-ag-filter-clear';
    private static readonly VISIBLE_CLASS = 'vip-ag-filter-clear--visible';

    private readonly zone = inject(NgZone);
    private readonly destroyRef = inject(DestroyRef);
    private readonly translateService = inject(TranslateService);

    private observer?: MutationObserver;
    private scheduled = false;

    /**
     * Nastartuje sledování. Volá se jednorázově z AppComponent, opakované volání
     * je bez efektu.
     */
    init(): void {
        if (this.observer || typeof MutationObserver === 'undefined') {
            return;
        }

        // Mimo Angular zónu - jinak by každá mutace DOM (i scroll v gridu)
        // spouštěla change detection.
        this.zone.runOutsideAngular(() => {
            this.observer = new MutationObserver(mutations => {
                if (mutations.some(m => this.hasRelevantNode(m.addedNodes))) {
                    this.schedule();
                }
            });
            this.observer.observe(document.body, { childList: true, subtree: true });
            this.decorateAll();
        });

        this.destroyRef.onDestroy(() => {
            this.observer?.disconnect();
            this.observer = undefined;
        });
    }

    /**
     * Přidal se do DOM filter popup? Bez této kontroly by se `decorateAll()`
     * spouštěl i při scrollování gridu, kde ag-grid průběžně přidává řádky.
     */
    private hasRelevantNode(nodes: NodeList): boolean {
        for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i];
            if (!(node instanceof Element)) {
                continue;
            }
            if (node.matches(AgGridFilterClearService.POPUP_SELECTOR)) {
                return true;
            }
            // Řádky a buňky se mění nejčastěji a filtr v sobě nikdy nemají.
            if (node.classList.contains('ag-row') || node.classList.contains('ag-cell')) {
                continue;
            }
            if (node.querySelector('.ag-filter-body')) {
                return true;
            }
        }
        return false;
    }

    /** Sloučí dávku mutací do jednoho průchodu na následující frame. */
    private schedule(): void {
        if (this.scheduled) {
            return;
        }
        this.scheduled = true;
        requestAnimationFrame(() => {
            this.scheduled = false;
            this.decorateAll();
        });
    }

    private decorateAll(): void {
        const inputs = document.querySelectorAll<HTMLInputElement>(
            AgGridFilterClearService.INPUT_SELECTOR
        );
        inputs.forEach(input => this.decorate(input));
    }

    private decorate(input: HTMLInputElement): void {
        if (input.dataset[AgGridFilterClearService.DECORATED_FLAG]) {
            return;
        }

        const wrapper = input.parentElement;
        if (!wrapper) {
            return;
        }
        input.dataset[AgGridFilterClearService.DECORATED_FLAG] = '1';

        const button = document.createElement('button');
        button.type = 'button';
        button.className = AgGridFilterClearService.BUTTON_CLASS;
        button.tabIndex = -1;
        const label = this.translateService.instant('agGridTranslations.clearFilter');
        button.setAttribute('aria-label', label);
        button.title = label;
        button.innerHTML = '<i class="fal fa-times"></i>';

        // Bez preventDefault by ag-grid zavřel popup ještě před klikem.
        button.addEventListener('mousedown', event => event.preventDefault());

        button.addEventListener('click', event => {
            event.preventDefault();
            event.stopPropagation();
            input.value = '';
            input.dispatchEvent(new Event('input', { bubbles: true }));
            this.toggle(input, button);
            input.focus();
        });

        input.addEventListener('input', () => this.toggle(input, button));

        wrapper.appendChild(button);
        this.toggle(input, button);
    }

    private toggle(input: HTMLInputElement, button: HTMLButtonElement): void {
        button.classList.toggle(AgGridFilterClearService.VISIBLE_CLASS, !!input.value);
    }
}
